import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import ConsumptionTypeService from '../services/ConsumptionTypeService';
import { isEmpty } from 'lodash';

const initialState = {
  data: [],
  datas: [],
  dropdownOptions: [],
  formError: [],
  pagination: {
    page: 0,
    per_page: 10,
    total: 10,
    totalPages: 10,
  },
  loading: false,
  dropdownLoading: false,
  isSuccess: false,
  submitLoading: false,
  isOpenConsumptionTypeDialog: false,
};

export const fetchConsumptionTypeList = createAsyncThunk(
  'consumptionType/fetchConsumptionTypeList',
  async ({ perPage, page, keyword, is_active }) => {
    try {
      const resp = await ConsumptionTypeService.List({ perPage, page, keyword, is_active });
      return resp;
    } catch (e) {
      return e.response;
    }
  },
);

export const fetchConsumptionTypeDropdown = createAsyncThunk(
  'consumptionType/fetchConsumptionTypeDropdown',
  async () => {
    try {
      const resp = await ConsumptionTypeService.DropdownList();
      return resp;
    } catch (e) {
      return e.response;
    }
  },
);

export const createConsumptionType = createAsyncThunk(
  'consumptionType/createConsumptionType',
  async (data, { rejectWithValue }) => {
    try {
      const resp = await ConsumptionTypeService.Create(data);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const updateConsumptionType = createAsyncThunk(
  'consumptionType/updateConsumptionType',
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await ConsumptionTypeService.Put(payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const deleteConsumptionType = createAsyncThunk(
  'consumptionType/deleteConsumptionType',
  async (id, { rejectWithValue }) => {
    try {
      const resp = await ConsumptionTypeService.Delt(id);
      return { resp, id };
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

const consumptionTypeSlice = createSlice({
  name: 'consumptionType',
  initialState,
  reducers: {
    openConsumptionTypeDialog: (state, action) => {
      state.isOpenConsumptionTypeDialog = action.payload;
    },
    resetConsumptionTypeFormError: (state) => {
      state.formError = [];
      state.isSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConsumptionTypeList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchConsumptionTypeList.fulfilled, (state, action) => {
        if (action.payload?.status === 200) {
          state.datas = [...(action.payload?.data?.data?.consumptionTypeList || [])];
          state.pagination = action.payload?.data?.data?.pagination;
        } else {
          state.error = action.payload?.data?.message;
          toast.error(state.error);
        }
        state.loading = false;
      })
      .addCase(fetchConsumptionTypeList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchConsumptionTypeDropdown.pending, (state) => {
        state.dropdownLoading = true;
      })
      .addCase(fetchConsumptionTypeDropdown.fulfilled, (state, action) => {
        if (action.payload?.status === 200) {
          const list = action.payload?.data?.data || [];
          state.dropdownOptions = list.map((item) => ({
            value: item.id,
            label: item.name,
          }));
        }
        state.dropdownLoading = false;
      })
      .addCase(fetchConsumptionTypeDropdown.rejected, (state) => {
        state.dropdownLoading = false;
      })
      .addCase(createConsumptionType.pending, (state) => {
        state.submitLoading = true;
        state.isSuccess = false;
      })
      .addCase(createConsumptionType.fulfilled, (state, action) => {
        state.datas.unshift(action.payload.data.data);
        state.isSuccess = true;
        toast.success(action.payload?.data?.message || action.payload?.message);
        state.pagination = {
          ...state.pagination,
          total: state.pagination.total + 1,
        };
        state.submitLoading = false;
        state.formError = [];
      })
      .addCase(createConsumptionType.rejected, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = false;
        const errors = action.payload?.data?.errors;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data?.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      })
      .addCase(updateConsumptionType.pending, (state) => {
        state.isSuccess = false;
        state.submitLoading = true;
      })
      .addCase(updateConsumptionType.fulfilled, (state, action) => {
        const index = state.datas.findIndex(
          (item) => item.id === action.payload.data.data.id,
        );
        if (index !== -1) {
          state.datas[index] = {
            ...state.datas[index],
            ...action.payload.data.data,
          };
        }
        state.isSuccess = true;
        state.submitLoading = false;
        state.formError = [];
        toast.success(action.payload?.data?.message);
      })
      .addCase(updateConsumptionType.rejected, (state, action) => {
        state.submitLoading = false;
        const errors = action.payload?.data?.errors;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data?.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      })
      .addCase(deleteConsumptionType.pending, (state) => {
        state.submitLoading = true;
      })
      .addCase(deleteConsumptionType.fulfilled, (state, action) => {
        const deletedId = action.payload?.id ?? action.meta.arg;
        state.datas = state.datas.filter((item) => item.id !== deletedId);
        toast.success(action.payload?.resp?.data?.message || action.payload?.data?.message);
        state.submitLoading = false;
        state.isOpenConsumptionTypeDialog = false;
        state.pagination = {
          ...state.pagination,
          total: state.pagination.total > 0 ? state.pagination.total - 1 : 0,
        };
      })
      .addCase(deleteConsumptionType.rejected, (state, action) => {
        state.submitLoading = false;
        state.isOpenConsumptionTypeDialog = false;
        toast.error(action.payload?.data?.message || 'Something went wrong');
      });
  },
});

const { reducer } = consumptionTypeSlice;

export const { openConsumptionTypeDialog, resetConsumptionTypeFormError } = consumptionTypeSlice.actions;

export const consumptionTypeSelector = (state) => state?.consumptionType;

export default reducer;
